import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { hasResource } from '../../../../utils/resourceAuth';
import { ResourceCode } from '../../../../constants/resourceCodes';
import {
  OPS_TOOL_BILLING_PERIOD_INSTANCES,
  OPS_TOOL_SETTLEMENT_FEE_ITEMS,
  OPS_TOOL_UPDATE_BILLING_PERIOD_FEE_ITEMS,
} from '../graphql/opsToolSettlementGraphql';
import { useOpsToolScenarioFilters } from '../hooks/useOpsToolScenarioFilters';

type BillingPeriodRow = {
  id: string;
  settlementClientName: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  status: number;
  isIssued: number;
  feeItems: { id: string; code: string; name: string }[];
};

type UpdateMode = 'SINGLE' | 'BATCH' | 'ALL';

const BillingPeriodFeeItemTab: React.FC = () => {
  const canOperate = hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]);
  const filters = useOpsToolScenarioFilters();
  const {
    businessScenarioCode,
    setBusinessScenarioCode,
    billType,
    setBillType,
    billTypeOptions,
    settlementClientId,
    setSettlementClientId,
    periodRange,
    setPeriodRange,
    scenarioOptions,
    scenariosLoading,
    clientOptions,
    clientsLoading,
    buildFilters,
  } = filters;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updateMode, setUpdateMode] = useState<UpdateMode>('BATCH');
  const [targetInstanceId, setTargetInstanceId] = useState<string | null>(null);

  const [updateForm] = Form.useForm();

  const { data: feeItemsData } = useQuery(OPS_TOOL_SETTLEMENT_FEE_ITEMS, {
    skip: !businessScenarioCode,
    variables: { businessScenarioCode: businessScenarioCode! },
  });

  const [loadInstances, { data: instancesData, loading: instancesLoading, refetch: refetchInstances }] =
    useLazyQuery(OPS_TOOL_BILLING_PERIOD_INSTANCES);
  const [updateFeeItems, { loading: updatingFeeItems }] = useMutation(OPS_TOOL_UPDATE_BILLING_PERIOD_FEE_ITEMS);

  const mainFeeItemOptions = useMemo(
    () =>
      (feeItemsData?.opsToolSettlementFeeItems ?? []).map(
        (item: { id: string; code: string; name: string }) => ({
          value: String(item.id),
          label: `${item.name} (${item.code})`,
        })
      ),
    [feeItemsData]
  );

  const rows: BillingPeriodRow[] = useMemo(
    () =>
      (instancesData?.opsToolBillingPeriodInstances?.content ?? []).map((row: BillingPeriodRow) => ({
        ...row,
        id: String(row.id),
      })),
    [instancesData]
  );

  const totalElements = instancesData?.opsToolBillingPeriodInstances?.totalElements ?? 0;

  const fetchInstances = (nextPage = page, nextSize = pageSize) => {
    if (businessScenarioCode == null || billType == null) return;
    loadInstances({
      variables: {
        filters: buildFilters(),
        pageRequest: { page: nextPage, size: nextSize },
      },
    });
  };

  useEffect(() => {
    if (businessScenarioCode == null || billType == null) return;
    fetchInstances(1, pageSize);
    setPage(1);
    setSelectedRowKeys([]);
  }, [businessScenarioCode, billType, settlementClientId, periodRange]);

  const isAppendMode = updateMode === 'BATCH' || updateMode === 'ALL';

  const openUpdateModal = (mode: UpdateMode, instanceId?: string, presetFeeItemIds?: string[]) => {
    setUpdateMode(mode);
    setTargetInstanceId(instanceId ?? null);
    updateForm.setFieldsValue({
      feeItemIds: mode === 'SINGLE' ? (presetFeeItemIds ?? []) : [],
    });
    setUpdateModalOpen(true);
  };

  const handleUpdateFeeItems = async () => {
    if (businessScenarioCode == null) return;
    try {
      const values = await updateForm.validateFields();
      const filterValues = buildFilters();
      const input: Record<string, unknown> = {
        updateMode,
        businessScenarioCode,
        billType: filterValues.billType,
        settlementClientId: filterValues.settlementClientId,
        periodStartFrom: filterValues.periodStartFrom ?? null,
        periodEndTo: filterValues.periodEndTo ?? null,
        feeItemIds: (values.feeItemIds as string[]).map(Number),
      };
      if (updateMode === 'SINGLE') input.instanceIds = [String(targetInstanceId)]; // ID 超出JS安全整数，以字符串传输
      else if (updateMode === 'BATCH') input.instanceIds = selectedRowKeys.map(k => String(k));

      const result = await updateFeeItems({ variables: { input } });
      const payload = result.data?.opsToolUpdateBillingPeriodFeeItems;
      if (!payload?.success) {
        message.error(payload?.message ?? (isAppendMode ? '添加失败' : '更新失败'));
        return;
      }
      message.success(
        isAppendMode
          ? `添加成功，共 ${payload.updatedCount ?? 0} 条账期已追加费用项`
          : `更新成功，共 ${payload.updatedCount ?? 0} 条账期`
      );
      setUpdateModalOpen(false);
      setSelectedRowKeys([]);
      refetchInstances();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  const modalTitle =
    updateMode === 'SINGLE'
      ? '更新单个账期费用项'
      : updateMode === 'BATCH'
        ? '批量账期添加费用项'
        : '全部账期添加费用项';

  const feeItemFieldLabel = isAppendMode
    ? '要新增的主费用项（追加到 fee_item_ids_json，不覆盖已有项）'
    : '主费用项（完整列表，将写入 fee_item_ids_json）';

  const columns: ColumnsType<BillingPeriodRow> = [
    { title: '账期 ID', dataIndex: 'id', width: 100 },
    {
      title: '客户',
      dataIndex: 'settlementClientName',
      width: 180,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '账期开始',
      dataIndex: 'periodStart',
      width: 170,
      render: (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—'),
    },
    {
      title: '账期结束',
      dataIndex: 'periodEnd',
      width: 170,
      render: (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—'),
    },
    { title: '状态', dataIndex: 'status', width: 80, render: (v: number) => <Tag>{v}</Tag> },
    {
      title: '已出账',
      dataIndex: 'isIssued',
      width: 80,
      render: (v: number) => (v === 1 ? <Tag color="green">是</Tag> : <Tag>否</Tag>),
    },
    {
      title: '已配置的费用项',
      dataIndex: 'feeItems',
      render: (items: BillingPeriodRow['feeItems']) =>
        items?.length ? (
          <Space size={[4, 4]} wrap>
            {items.map(item => (
              <Tag key={item.id}>{item.name} ({item.code})</Tag>
            ))}
          </Space>
        ) : (
          <span className="text-gray-400">未配置</span>
        ),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) =>
        canOperate ? (
          <Button
            type="link"
            size="small"
            onClick={() => openUpdateModal('SINGLE', record.id, record.feeItems.map(f => f.id))}
          >
            更新费用项
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <Space wrap className="mb-4">
        <Select
          style={{ width: 220 }}
          placeholder="业务场景"
          loading={scenariosLoading}
          value={businessScenarioCode}
          options={scenarioOptions}
          onChange={setBusinessScenarioCode}
        />
        <Select
          style={{ width: 160 }}
          placeholder="账单类型"
          value={billType}
          options={billTypeOptions}
          onChange={setBillType}
        />
        <Select
          style={{ width: 260 }}
          placeholder="客户"
          loading={clientsLoading}
          value={settlementClientId}
          options={clientOptions}
          onChange={setSettlementClientId}
          showSearch
          optionFilterProp="label"
        />
        <DatePicker.RangePicker value={periodRange} onChange={v => setPeriodRange(v)} allowEmpty={[true, true]} />
        <Button icon={<ReloadOutlined />} onClick={() => { setPage(1); setSelectedRowKeys([]); fetchInstances(1, pageSize); }}>
          查询
        </Button>
        {canOperate && (
          <>
            <Button type="primary" disabled={selectedRowKeys.length === 0} onClick={() => openUpdateModal('BATCH')}>
              批量账期添加费用项 ({selectedRowKeys.length})
            </Button>
            <Button onClick={() => openUpdateModal('ALL')}>全部账期添加费用项</Button>
          </>
        )}
      </Space>

      <Table
        rowKey="id"
        loading={instancesLoading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 1200 }}
        rowSelection={canOperate ? { selectedRowKeys, onChange: setSelectedRowKeys } : undefined}
        pagination={{
          current: page,
          pageSize,
          total: totalElements,
          showSizeChanger: true,
          onChange: (p, s) => { setPage(p); setPageSize(s); fetchInstances(p, s); },
        }}
      />

      <Modal
        title={modalTitle}
        open={updateModalOpen}
        onCancel={() => setUpdateModalOpen(false)}
        onOk={handleUpdateFeeItems}
        confirmLoading={updatingFeeItems}
        width={640}
        destroyOnClose
      >
        <Form form={updateForm} layout="vertical">
          <Form.Item
            name="feeItemIds"
            label={feeItemFieldLabel}
            rules={[{ required: true, message: '请选择至少一个主费用项' }]}
          >
            <Select mode="multiple" options={mainFeeItemOptions} placeholder="请选择主费用项" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default BillingPeriodFeeItemTab;
